import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import type { EditorState } from '@reactvideoeditor/react-video-editor/types';
import { orgUiPath } from '@/core/api';
import { useOrgId } from '@/hooks/useOrgId';
import { EditorNeoShell } from '@/layouts/EditorNeoShell';
import {
  type CreativeEditorLocationState,
  resolveCreativeEditorBackTarget,
} from '@/rve/editorBackNav';
import { createAspectlyNeoAdaptors } from '@/rve/neoAdaptors';
import { neoProjectId } from '@/rve/neoProjectId';
import { unwrapNeoEditorState, wrapNeoEditorState } from '@/rve/neoPersistence';
import { getCreative, updateCreative } from '@/services/creatives';
import type { Creative } from '@/types/api';
import { Spinner } from '@/components/primitives/Spinner';

interface NeoEditorLoaderProps {
  orgId: string;
  creativeId: number;
}

function NeoEditorLoader({ orgId, creativeId }: NeoEditorLoaderProps) {
  const location = useLocation();
  const [creative, setCreative] = useState<Creative | null>(null);
  const [bootEditorState, setBootEditorState] = useState<EditorState | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getCreative(creativeId)
      .then((row) => {
        if (cancelled) return;
        if (String(row.organization_id) !== orgId) {
          setError('Creative does not belong to this workspace');
          return;
        }
        setCreative(row);
        setBootEditorState(unwrapNeoEditorState(row.neo));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load creative');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [creativeId, orgId]);

  const handlePersist = useCallback(
    async (state: EditorState) => {
      setSaveError(null);
      try {
        const updated = await updateCreative(creativeId, {
          neo: wrapNeoEditorState(state),
        });
        setCreative(updated);
      } catch (err: unknown) {
        setSaveError(err instanceof Error ? err.message : 'Failed to save Neo state');
      }
    },
    [creativeId],
  );

  const handleDurationChange = useCallback(
    async (ms: number) => {
      setSaveError(null);
      try {
        const newFrames = Math.max(1, Math.round((ms / 1000) * 30)); // 30 fps
        
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let nextNeo = creative?.neo as any;
        if (nextNeo?.creative?.tracks) {
          nextNeo = {
            ...nextNeo,
            creative: {
              ...nextNeo.creative,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              tracks: nextNeo.creative.tracks.map((track: any) => ({
                ...track,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                items: track.items.map((item: any) => ({
                  ...item,
                  durationInFrames: newFrames,
                })),
              })),
            },
          };
        }

        const updated = await updateCreative(creativeId, {
          render_duration_ms: ms,
          neo: nextNeo,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any);
        setCreative(updated);
        setBootEditorState(unwrapNeoEditorState(updated.neo));
      } catch (err: unknown) {
        setSaveError(err instanceof Error ? err.message : 'Failed to save duration');
      }
    },
    [creativeId, creative],
  );

  if (loading) {
    return (
      <div className="aspectly:flex aspectly:h-screen aspectly:items-center aspectly:justify-center aspectly:bg-surface">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || creative == null || creative.id == null) {
    return (
      <div className="aspectly:flex aspectly:h-screen aspectly:flex-col aspectly:items-center aspectly:justify-center aspectly:gap-4 aspectly:bg-surface aspectly:px-6">
        <p className="aspectly:text-on-surface-variant aspectly:text-sm">
          {error || 'Creative not found'}
        </p>
        <Link
          to={orgUiPath(orgId, '/creatives')}
          className="aspectly:text-sm aspectly:text-primary aspectly:underline"
        >
          Back to creatives
        </Link>
      </div>
    );
  }

  return (
    <>
      {saveError ? (
        <div className="aspectly:fixed aspectly:top-4 aspectly:right-4 aspectly:z-[60] aspectly:rounded-lg aspectly:bg-error-container aspectly:px-4 aspectly:py-2 aspectly:text-sm aspectly:text-on-error-container">
          {saveError}
        </div>
      ) : null}
      <EditorNeoShell
        projectId={neoProjectId(orgId, creative.id)}
        adaptors={createAspectlyNeoAdaptors(creative.organization_id)}
        editorState={bootEditorState}
        onPersist={handlePersist}
        durationMs={creative.render_duration_ms ?? undefined}
        onDurationChange={handleDurationChange}
        backTo={resolveCreativeEditorBackTarget(
          orgId,
          location.state as CreativeEditorLocationState | null,
          creative,
          location.search,
        )}
      />
    </>
  );
}

export default function CreativeEditorNeoPage() {
  const orgId = useOrgId();
  const { creativeId: creativeIdParam } = useParams();
  const creativeId = creativeIdParam != null ? parseInt(creativeIdParam, 10) : undefined;
  const isInvalidId = creativeId === undefined || Number.isNaN(creativeId);

  if (isInvalidId) {
    return (
      <div className="aspectly:flex aspectly:h-screen aspectly:flex-col aspectly:items-center aspectly:justify-center aspectly:gap-4 aspectly:bg-surface aspectly:px-6">
        <p className="aspectly:text-on-surface-variant aspectly:text-sm">Invalid creative id</p>
        <Link
          to={orgUiPath(orgId, '/creatives')}
          className="aspectly:text-sm aspectly:text-primary aspectly:underline"
        >
          Back to creatives
        </Link>
      </div>
    );
  }

  return <NeoEditorLoader key={`${orgId}-${creativeId}`} orgId={orgId} creativeId={creativeId} />;
}
